import axios from 'axios';
import { updateConnectionStatus } from 'actions/app';
import { history } from 'containers/App';
import EnvConfig from 'env.config';
import { detectLocale } from 'common/language/localeHelper';
import { isTranslatedForm } from 'common/forms/formTypeHelper';

const {
  tokenName,
  baseApiUrl,
  apiEndpoints: {
    authentaLink: authentaLinkUri,
    login: loginUri,
    logout: logoutUri,
    forms: formsUri,
    language: languageUri,
    config: configUri,
    auth: authUri,
    validatePin: validatePinUri,
    supportingDocuments: supportingDocumentsUri,
    questionnaire: questionnaireUri,
    formGroups: formGroupsUri,
  }
} = EnvConfig;

// const HTTP_400_BADREQUEST = 400;
const HTTP_401_UNAUTHORIZED = 401;
const HTTP_403_FORBIDDEN = 403;
// const HTTP_404_NOTFOUND = 404;
const HTTP_408_REQUESTTIMEOUT = 408;
const HTTP_429_TOOMANYREQUESTS = 429;
const HTTP_498_SESSIONTIMEOUT = 498;
const HTTP_501_NOTIMPLEMENTED = 501;
const HTTP_502_BADGATEWAY = 502;
const HTTP_503_SERVICEUNAVAILABLE = 503;
// const HTTP_504_GATEWAYTIMEOUT = 504;

const getDefaultConfig = () => {
  const locale = detectLocale();
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept-Language': locale
    },
    withCredentials: true
  };

  return config;
};

const getDspConfig = (headers) => {
  const config = { headers, withCredentials: true };
  config.headers['Content-Type'] = 'application/json';
  return config;
};

// eslint-disable-next-line import/prefer-default-export
export class ApiService {
  static axios = axios.create({
    baseUrl: baseApiUrl
  });

  // AUTH API

  static getAuthTokenName() {
    return tokenName;
  }

  static getBaseApiUrl() {
    return baseApiUrl;
  }

  static getLoginUrl(queryString) {
    return `${ baseApiUrl }${ loginUri }${ queryString || '' }`;
  }

  static getLogoutUrl() {
    return `${ baseApiUrl }${ logoutUri }`;
  }

  static postAuthentaLink(formData) {
    return ApiService.axios.post(
      `${ baseApiUrl }${ authentaLinkUri }`,
      formData,
      getDefaultConfig()
    );
  }

  // LANGUAGE API

  static getLanguages() {
    return ApiService.axios.get(
      `${ baseApiUrl }${ languageUri }`,
      getDefaultConfig()
    );
  }

  static async getLanguage(language) {
    const config = getDefaultConfig();
    return ApiService.axios.get(
      `${ baseApiUrl }${ languageUri }/${ language }`,
      config
    );
  }

  static getAuthState() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ authUri }/state`,
        getDefaultConfig()
      );
  }

  // TAX TREATY API

  static getSpecialRateProvisions(treatyCountry, hasPensionClaim) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/specialrateprovisions/${ treatyCountry }?hasPensionClaim=${ hasPensionClaim }`,
        getDefaultConfig()
      );
  }

  static getOtherArticles() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/otherarticles`,
        getDefaultConfig()
      );
  }

  // JURISDICTIONS API

  static getJurisdictions() {
    const config = { ...getDefaultConfig() };
    return ApiService.axios.get(
      `${ baseApiUrl }${ configUri }/jurisdictions`,
      config
    );
  }

  static getDspConsole(url, headers) {
    const config = getDspConfig(headers);
    return ApiService.axios
      .post(
        `${ url }`,
        null,
        config
      );
  }

  static async getOnlineConfig() {
    return ApiService.axios.get(
      `${ baseApiUrl }${ configUri }`,
      getDefaultConfig()
    );
  }

  static getPaymentTypes() {
    return ApiService.axios.get(
      `${ baseApiUrl }${ configUri }/payment-types`,
      getDefaultConfig()
    );
  }

  // FORM API

  static addForm(formData) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }`,
        formData,
        getDefaultConfig()
      );
  }

  static getFormById(formType, formId) {
    const config = getDefaultConfig();
    if (!isTranslatedForm(formType)) {
      config.headers['Accept-Language'] = 'en';
    }
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/${ formId }`,
        config
      );
  }

  static validateForm(formType, formId, formData) {
    const config = getDefaultConfig();
    if (!isTranslatedForm(formType)) {
      config.headers['Accept-Language'] = 'en';
    }
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formType }/${ formId }/validate`,
        formData,
        config
      );
  }

  static validateQuestionnaireForm(formType, formId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ questionnaireUri }/${ formType }/${ formId }/validate`,
        formData,
        getDefaultConfig()
      );
  }

  static getFormPdf(formId, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ formsUri }/${ formId }/pdf/download`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getPreviewPdf(formId, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ formsUri }/${ formId }/pdf/preview`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getCollectableSupportingDocPdf(requestData, isPreview, config) {
    const requestUrl = isPreview ? 'preview' : `${ requestData.supportingDocId }/download`;
    return ApiService.axios({
      url: `${ baseApiUrl }${ supportingDocumentsUri }/${ requestUrl }`,
      method: isPreview ? 'post' : 'get',
      data: isPreview ? requestData : null,
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getSignedCollectableSupportingDocPdf(formId, requestData, isPreview, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ supportingDocumentsUri }/${ formId }/pdf/download`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static confirm(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }/${ id }/confirm`,
        null,
        getDefaultConfig()
      );
  }

  static submit(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }/${ id }/submit`,
        null,
        getDefaultConfig()
      );
  }

  static certify(id) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ id }/certify`,
        null,
        getDefaultConfig()
      );
  }

  static revert(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }/${ id }/revert`,
        null,
        getDefaultConfig()
      );
  }

  static sign(id, printName, capacity, signDate, collectableSupportingDocData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ id }/sign`,
        {
          printName,
          capacity,
          signDate,
          collectableSupportingDocData
        },
        getDefaultConfig()
      );
  }

  // FILE UPLOAD API

  static getFileUploadUrl() {
    return `${ baseApiUrl }${ supportingDocumentsUri }`;
  }

  static deleteFileUpload(guid) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }/file/upload/${ guid }`,
        getDefaultConfig()
      );
  }

  // SUPPORTING DOCUMENTS API

  static deleteSupportingDoc(id) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }${ supportingDocumentsUri }/${ id }`,
        getDefaultConfig()
      );
  }

  static sendPinToRecipient(pinType) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ authUri }/sendpin`,
        {
          pinType
        },
        getDefaultConfig()
      );
  }

  static sendPinConfirmation(pinType, resendReference) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ authUri }/sendnoemailpin`,
        {
          pinType,
          resendReference
        },
        getDefaultConfig()
      );
  }

  static validatePin(pin, pinType) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validatePinUri }`,
        {
          pin,
          pinType
        },
        getDefaultConfig()
      );
  }

  static getFileNameFromHeaders(headers) {
    const disposition = headers && headers['content-disposition'];

    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        const filename = matches[1].replace(/['"]/g, '');
        return (filename || 'form.pdf');
      }
    }
    return 'form.pdf';
  }

  // FORM GROUPS API

  static addFormGroup(formGroupData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formGroupsUri }`,
        formGroupData,
        getDefaultConfig()
      );
  }

  static getFormGroupById(formType, formGroupId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formGroupsUri }/${ formGroupId }`,
        getDefaultConfig()
      );
  }

  static validateFormGroupQuestionnaire(formGroupType, formGroupId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formGroupsUri }/${ formGroupType }/${ formGroupId }/validate`,
        formData,
        getDefaultConfig()
      );
  }

  static submitFormGroup(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formGroupsUri }/${ id }/submit`,
        null,
        getDefaultConfig()
      );
  }

  static getFormGroupFormPreviewPdf(formGroupId, formType, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ formGroupsUri }/${ formGroupId }/${ formType }/pdf/preview`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getFormLinkedCollectableSupportingDocId(uploadedFormId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ supportingDocumentsUri }/${ uploadedFormId }/form/getLinkedCollectableDocId`,
        getDefaultConfig()
      );
  }

  static getFormGroupLinkedCollectableSupportingDocId(formGroupId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ supportingDocumentsUri }/${ formGroupId }/formGroup/getLinkedCollectableDocId`,
        getDefaultConfig()
      );
  }

  static confirmFormGroup(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formGroupsUri }/${ id }/confirm`,
        null,
        getDefaultConfig()
      );
  }

  static certifyFormGroup(id) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formGroupsUri }/${ id }/certify`,
        null,
        getDefaultConfig()
      );
  }

  static signFormGroup(id, printName, capacity, signDate, collectableSupportingDocData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formGroupsUri }/${ id }/sign`,
        {
          printName,
          capacity,
          signDate,
          collectableSupportingDocData
        },
        getDefaultConfig()
      );
  }

  static getFormGroupPdf(formGroupId, formType, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ formGroupsUri }/${ formGroupId }/${ formType }/pdf/download`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        if (/CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent)) {
          // Chrome iOS specific
          const reader = new FileReader();
          reader.onloadend = function () {
            let url = reader.result;
            const popup = window.open(url, '_blank');
            if (!popup) {
              window.location.href = url;
            }
            url = undefined; // release reference before dispatching
          };
          reader.readAsDataURL(blob);
          return null;
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          // IE specific
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        // All other browsers
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static revertFormGroup(id) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formGroupsUri }/${ id }/revert`,
        null,
        getDefaultConfig()
      );
  }

  static getSupportingDocsByFormGroupId(formGroupId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formGroupsUri }/${ formGroupId }/supporting-docs`,
        getDefaultConfig()
      );
  }

  static getIgaSubClassificationCombos() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/igasubclassifications`,
        getDefaultConfig()
      );
  }

  static getStockMarkets() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/stockmarkets`,
        getDefaultConfig()
      );
  }

  static getLobs() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/lobs`,
        getDefaultConfig()
      );
  }

  /* Retry Code */
  static queuedPromises = [];

  static isProcessingQueue = false;

  static startQueuedRequestRetryTimeout = 0;

  static globalRetryCount = 0;

  static addQueuedPromise(promise, error) {
    if (promise != null) {
      this.queuedPromises.push(promise);
    }
  }

  static retryPromise(promise) {
    if (!promise) {
      return Promise.reject(new Error('Empty promise'));
    }

    ApiService.globalRetryCount += 1;
    return Promise.resolve(promise());
  }

  static retryRequest(request) {
    if (!request) {
      return Promise.reject(new Error('Empty promise'));
    }

    const requestConfig = { ...request, retries: (request.retries || 0) + 1 };
    ApiService.globalRetryCount += 1;
    return Promise.resolve(ApiService.axios.request(requestConfig));
  }

  static delayRetryRequest(ms, requestConfig) {
    return new Promise((resolve) => { setTimeout(resolve, ms); }).then(() => ApiService.retryRequest(requestConfig));
  }

  static processQueuedPromises(bypassCheck) {
    // console.log(`processQueuedPromises - bypass: ${ bypassCheck || false }, isProcessingQueue: ${ ApiService.isProcessingQueue }, queuedPromises(length): ${ ApiService.queuedPromises.length }`);
    if (!bypassCheck && this.isProcessingQueue) {
      return;
    }

    this.isProcessingQueue = true;
    this.startQueuedRequestRetryTimeout = 0;
    clearTimeout(this.startQueuedRequestRetryTimeout);

    if (this.queuedPromises && this.queuedPromises.length > 0) {
      const request = this.queuedPromises.pop();
      this.retryPromise(
        request
      ).then(() => {
        // Should only hit here if successful?
        this.startQueuedRequestRetryTimeout = 0;
        clearTimeout(this.startQueuedRequestRetryTimeout);
        this.isProcessingQueue = false;
        if (ApiService.globalRetryCount < 2) {
          this.processQueuedPromises(false);
        }
        // Promise.resolve(response);
      }).catch((error) => {
        this.isProcessingQueue = false;
        Promise.reject(error);
      });
    } else {
      this.isProcessingQueue = false;
    }
  }
}

ApiService.axios.interceptors.response.use(
  (response) => {
    ApiService.processQueuedPromises(false);
    ApiService.globalRetryCount = 0;
    updateConnectionStatus(true, 0);
    return response;
  },
  (error) => {
    if (error.response) {
      if (error.response.status === HTTP_401_UNAUTHORIZED) {
        const url = error.response.config && error.response.config.url;
        if (!url.startsWith(`${ baseApiUrl }${ configUri }`)) {
          history.push('/unauthorized');
        } else {
          window.location = ApiService.getLoginUrl(window.location.search);
        }
      }
      if (error.response.status === HTTP_403_FORBIDDEN) {
        if (error.response.data && error.response.data.code === HTTP_498_SESSIONTIMEOUT) {
          history.push('/sessiontimeout');
        } else {
          history.push('/unlicensed');
        }
      }
      if (error.response.status === HTTP_429_TOOMANYREQUESTS || error.response.status === HTTP_503_SERVICEUNAVAILABLE) {
        history.push('/unavailable');
      }

      if ([HTTP_408_REQUESTTIMEOUT,
        HTTP_429_TOOMANYREQUESTS,
        HTTP_501_NOTIMPLEMENTED,
        HTTP_502_BADGATEWAY,
        HTTP_503_SERVICEUNAVAILABLE].indexOf(error.response.status) > -1 && error.response.config) {
        if (error.response.config.retries >= 1) {
          error = { ...error, retries: error.response.config.retries };
          if (error.response.config.retries === 1) {
            return ApiService.delayRetryRequest(3 * 1000, error.response.config);
          }
        } else {
          return ApiService.retryRequest(error.response.config);
        }
      }
    } else if (error.message === 'Network Error') {
      updateConnectionStatus(false, ApiService.globalRetryCount);
      if (error.config && ApiService.globalRetryCount >= 1) {
        error = { ...error, retries: ApiService.globalRetryCount, message: error.message };
        if (error.retries === 1) {
          return ApiService.delayRetryRequest(3 * 1000, error.config);
        }
      } else {
        return ApiService.retryRequest(error.config);
      }
    }

    return Promise.reject(error);
  }
);
